'use client';

import { useState } from 'react';
import { AlertTriangle, CircleCheck, Loader2 } from 'lucide-react';

import type { CaminhoTransito, MotivoBloqueioTransito } from '../types/encerramento.types';
import { formatarData } from '../utils/formatarEncerramento';
import { classesDoTom } from '../utils/tonsEncerramento';

type EtapaPublicacao = 'confirmacao' | 'publicando' | 'sucesso' | 'falha';

interface DialogPublicarTransitoProps {
  readonly numeroProcesso: string;
  readonly caminho: CaminhoTransito;
  /** `null` enquanto a elegibilidade carrega, ou quando o guard não conseguiu calcular a data. */
  readonly vencimentoEm: string | null;
  readonly decididoEm?: string | null;
  readonly bloqueio?: MotivoBloqueioTransito | null;
  /**
   * Elegibilidade ainda carregando: não afirma prazo nem libera o botão.
   *
   * Estado que o mock não tinha — a tela nascia com prazo de `MOCK_INTIMACAO_SENTENCA`, então nunca
   * havia espera. Usa o padrão de texto discreto já presente no arquivo, e está registrado no PR
   * como estado inventado por ausência de design (regra #13).
   */
  readonly carregando?: boolean;
  /**
   * Falha ao CONSULTAR a elegibilidade — distinta da falha ao publicar (`etapa === 'falha'`).
   *
   * Sem este estado, a falha da consulta era indistinguível de "carregando" e o diálogo ficava em
   * "Verificando…" para sempre (SEC-01 da review do #971). Estado inventado por ausência de design:
   * reusa a caixa de erro e o botão "Tentar de novo" que a `FaixaEstadoPosSentenca` já usa, e está
   * registrado no PR para a designer aprovar (regra #13).
   */
  readonly erroElegibilidade?: string | null;
  readonly onTentarNovamente?: () => void;
  readonly onConfirmar: () => Promise<void>;
  readonly onFechar: () => void;
}

export function DialogPublicarTransito({
  numeroProcesso,
  caminho,
  vencimentoEm,
  decididoEm = null,
  bloqueio = null,
  carregando = false,
  erroElegibilidade = null,
  onTentarNovamente,
  onConfirmar,
  onFechar,
}: DialogPublicarTransitoProps) {
  const [etapa, setEtapa] = useState<EtapaPublicacao>('confirmacao');
  const [reafirmado, setReafirmado] = useState(false);
  const [mensagemFalha, setMensagemFalha] = useState<string | null>(null);

  const tomErro = classesDoTom('erro');
  const tomSucesso = classesDoTom('sucesso');
  // `carregando` e `erroElegibilidade` entram no predicado: sem elegibilidade RESOLVIDA, não se
  // afirma que pode publicar. Consulta que falhou não é permissão.
  const podeAfirmarContexto = bloqueio === null && erroElegibilidade === null && !carregando;
  // Composto, e não reescrito: com as três condições duplicadas, mexer numa delas só aqui
  // liberaria o botão sobre um contexto que a tela declara não poder afirmar.
  const podeConfirmar = podeAfirmarContexto && reafirmado && etapa === 'confirmacao';

  async function handleConfirmar() {
    setEtapa('publicando');
    setMensagemFalha(null);
    try {
      await onConfirmar();
      setEtapa('sucesso');
    } catch (causa) {
      setMensagemFalha(
        causa instanceof Error ? causa.message : 'Falha ao comunicar com o servidor.',
      );
      setEtapa('falha');
    }
  }

  if (etapa === 'sucesso') {
    return (
      <Moldura titulo="Processo encerrado" onFechar={onFechar}>
        <div className={`flex items-start gap-2.5 rounded-[8px] border p-3 ${tomSucesso.caixa}`}>
          <CircleCheck
            aria-hidden="true"
            className={`mt-0.5 h-4 w-4 shrink-0 ${tomSucesso.texto}`}
          />
          <p className="text-xs text-neutral-700">
            Certidão de trânsito em julgado publicada. O processo {numeroProcesso} está finalizado
            por sentença arbitral.
          </p>
        </div>
        <Rodape>
          <BotaoPrimario onClick={onFechar}>Concluir</BotaoPrimario>
        </Rodape>
      </Moldura>
    );
  }

  if (etapa === 'falha') {
    return (
      <Moldura titulo="Não foi possível publicar" onFechar={onFechar}>
        <div
          className={`flex items-start gap-2.5 rounded-[8px] border p-3 ${tomErro.caixa}`}
          role="alert"
        >
          <AlertTriangle
            aria-hidden="true"
            className={`mt-0.5 h-4 w-4 shrink-0 ${tomErro.texto}`}
          />
          <div className="text-xs text-neutral-700">
            <p className={`font-medium ${tomErro.texto}`}>O processo NÃO foi encerrado.</p>
            <p>{mensagemFalha}</p>
          </div>
        </div>
        <Rodape>
          <BotaoSecundario onClick={onFechar}>Fechar</BotaoSecundario>
          <BotaoPrimario onClick={() => setEtapa('confirmacao')}>Tentar de novo</BotaoPrimario>
        </Rodape>
      </Moldura>
    );
  }

  return (
    <Moldura titulo="Publicar trânsito em julgado" onFechar={onFechar}>
      <p className="text-sm text-neutral-600">O processo será encerrado por sentença arbitral.</p>

      {/*
        Caminho e prazo só aparecem quando a tela PODE afirmá-los. Com bloqueio à vista, o diálogo
        se contradizia na mesma tela: dizia "Após decisão do árbitro" e "Decidido em data não
        informada" logo acima de "há pedido de esclarecimento e ainda sem decisão" (COR-11 da review
        do #971). Mesma disciplina de `podeAfirmarEstadoPosSentenca` — não afirmar o não verificável.
      */}
      {podeAfirmarContexto ? (
        <dl className="rounded-[8px] bg-neutral-50 p-3">
          <dt className="text-xs text-neutral-600">Caminho</dt>
          <dd className="mb-2 text-sm text-neutral-900">
            {caminho === 'silencioDaParte'
              ? 'Silêncio da parte no prazo de esclarecimento'
              : 'Após decisão do árbitro sobre o pedido de esclarecimento'}
          </dd>
          <dt className="text-xs text-neutral-600">Prazo</dt>
          <dd className="text-sm text-neutral-900">
            {textoDoPrazo(caminho, vencimentoEm, decididoEm, carregando)}
          </dd>
        </dl>
      ) : null}

      {erroElegibilidade ? (
        <div
          className={`flex items-start gap-2.5 rounded-[8px] border p-3 ${tomErro.caixa}`}
          role="alert"
        >
          <AlertTriangle
            aria-hidden="true"
            className={`mt-0.5 h-4 w-4 shrink-0 ${tomErro.texto}`}
          />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-neutral-700">
              Não conseguimos verificar se este processo pode transitar.
            </p>
            <p className="text-xs text-neutral-600">{erroElegibilidade}</p>
          </div>
          {onTentarNovamente ? (
            <button
              className="shrink-0 rounded-[8px] border border-neutral-300 px-3 py-1.5 text-xs text-neutral-700"
              onClick={onTentarNovamente}
              type="button"
            >
              Tentar de novo
            </button>
          ) : null}
        </div>
      ) : carregando ? (
        // `aria-live` porque a verificação é o que mantém "Publicar e encerrar" desabilitado: sem
        // região viva, quem usa leitor de tela encontra o botão morto e nenhuma explicação.
        // `polite`, e não `alert`: é progresso esperado, não falha — só o erro interrompe.
        <p
          aria-live="polite"
          className="rounded-[8px] border border-neutral-200 p-3 text-xs text-neutral-600"
        >
          Verificando se o processo pode transitar…
        </p>
      ) : bloqueio ? (
        <div
          className={`flex items-start gap-2.5 rounded-[8px] border p-3 ${tomErro.caixa}`}
          role="alert"
        >
          <AlertTriangle
            aria-hidden="true"
            className={`mt-0.5 h-4 w-4 shrink-0 ${tomErro.texto}`}
          />
          <p className="text-xs text-neutral-700">{textoBloqueio(bloqueio, vencimentoEm)}</p>
        </div>
      ) : (
        <label
          className={`flex cursor-pointer items-start gap-2.5 rounded-[8px] border p-3 ${tomErro.caixa}`}
        >
          <input
            checked={reafirmado}
            className="mt-0.5 h-4 w-4 shrink-0 rounded-[4px]"
            onChange={(evento) => setReafirmado(evento.target.checked)}
            type="checkbox"
          />
          <span className="text-xs text-neutral-700">
            Entendo que o processo {numeroProcesso} será encerrado definitivamente e que esta ação
            não pode ser desfeita.
          </span>
        </label>
      )}

      <Rodape>
        <BotaoSecundario onClick={onFechar}>Cancelar</BotaoSecundario>
        <BotaoPrimario desabilitado={!podeConfirmar} onClick={handleConfirmar}>
          {etapa === 'publicando' ? (
            <span className="flex items-center gap-1.5">
              <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
              Publicando…
            </span>
          ) : (
            'Publicar e encerrar'
          )}
        </BotaoPrimario>
      </Rodape>
    </Moldura>
  );
}

/**
 * O prazo só é afirmado quando existe data. Antes, `formatarData(vencimentoEm)` era chamado sempre
 * — e como a fonte era mock, a tela dizia "Vencido em <data>" para qualquer processo.
 */
function textoDoPrazo(
  caminho: CaminhoTransito,
  vencimentoEm: string | null,
  decididoEm: string | null,
  carregando: boolean,
): string {
  if (carregando) return 'Verificando…';
  if (caminho === 'decisaoDoArbitro') {
    return `Decidido em ${decididoEm ? formatarData(decididoEm) : 'data não informada'}`;
  }
  if (!vencimentoEm) return 'Data de vencimento indisponível';
  return `Vencido em ${formatarData(vencimentoEm)}, sem manifestação`;
}

function textoBloqueio(motivo: MotivoBloqueioTransito, vencimentoEm: string | null): string {
  if (motivo === 'prazoNaoVencido') {
    return vencimentoEm
      ? `O prazo de esclarecimento vence em ${formatarData(vencimentoEm)}. A certidão de trânsito não pode ser antecipada.`
      : 'O prazo de esclarecimento ainda não venceu. A certidão de trânsito não pode ser antecipada.';
  }
  if (motivo === 'manifestacaoPendente') {
    return 'Há pedido de esclarecimento registrado e ainda sem decisão do árbitro.';
  }
  if (motivo === 'calendarioAusente') {
    return 'Calendário de feriados do ano não cadastrado — a data de vencimento não é confiável.';
  }
  if (motivo === 'processoEncerrado') {
    return 'Este processo já está encerrado.';
  }
  return 'Apenas Secretaria ou Admin podem publicar o trânsito em julgado.';
}

function Moldura({
  titulo,
  children,
  onFechar,
}: {
  readonly titulo: string;
  readonly children: React.ReactNode;
  readonly onFechar: () => void;
}) {
  return (
    <div aria-labelledby="titulo-transito">
      <div className="bg-default-background w-full max-w-md space-y-3 rounded-[16px] border border-solid border-neutral-200 p-5">
        <h2 className="text-base font-medium text-neutral-900" id="titulo-transito">
          {titulo}
        </h2>
        {children}
      </div>
      <button className="sr-only" onClick={onFechar} type="button">
        Fechar
      </button>
    </div>
  );
}

function Rodape({ children }: { readonly children: React.ReactNode }) {
  return <div className="flex justify-end gap-2 pt-1">{children}</div>;
}

function BotaoPrimario({
  children,
  onClick,
  desabilitado = false,
}: {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
  readonly desabilitado?: boolean;
}) {
  return (
    <button
      className="bg-brand-600 rounded-[8px] px-3.5 py-2 text-sm text-white disabled:opacity-50"
      disabled={desabilitado}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}

function BotaoSecundario({
  children,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
}) {
  return (
    <button
      className="rounded-[8px] border border-neutral-300 px-3.5 py-2 text-sm text-neutral-700"
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}
